import { isValid } from "date-fns";
import React from "react";
import { UseFormRegisterReturn } from "react-hook-form";

import DatePickerWithRange from "../reusables/DatePickerWithRange";
import DateInput from "../reusables/custom-form-fields/DateInput";
import { DateTimePicker } from "../reusables/time-picker/date-timepicker";

type DatePickerProps = Omit<React.ComponentProps<typeof DateInput>, "type">;

type DateRangeProps = Omit<
  React.ComponentProps<typeof DatePickerWithRange>,
  "type"
>;

type DateTimePickerProps = Omit<
  React.ComponentProps<typeof DateTimePicker>,
  "type"
>;

type DateInputType = "date" | "date-time" | "date-range";

// Common props shared by both types
type BaseProps = {
  label?: string;
  error?: string;
  required?: boolean;
  register?: UseFormRegisterReturn;
  multiple?: boolean;
  disabled?: boolean;
  onChange: (date: Date | undefined) => void;
  defaultValue?: Date;
};

// Union type that changes based on the 'type' property
type FormDateInputProps =
  | (BaseProps & { type: "date" } & DatePickerProps)
  | (BaseProps & { type: "date-time" } & DateTimePickerProps)
  | (BaseProps & { type: "date-range" } & DateRangeProps);

const FormDateInput: React.FC<FormDateInputProps> = (props) => {
  const getField = (type: DateInputType): React.ReactNode => {
    switch (type) {
      case "date-time": {
        return (
          <DateTimePicker
            {...({ ...props } as BaseProps & {
              type: "date-time";
            } & DateTimePickerProps)}
            onChange={props.onChange}
            value={
              typeof props.defaultValue === "string" &&
              isValid(new Date(props.defaultValue))
                ? new Date(props.defaultValue)
                : props.defaultValue
            }
          />
        );
      }

      case "date-range":
        return (
          <DatePickerWithRange {...(props as BaseProps & DateRangeProps)} />
        );

      default:
        return (
          <DateInput
            {...(props as BaseProps & DatePickerProps)}
            onChange={(date) => props.onChange?.(date)}
          />
        );
    }
  };

  return getField(props.type);
};

export default FormDateInput;
